"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, TextField, useToast } from "@pharaday/lighthouse";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import useRequestPasswordReset from "../../../apis/useRequestPasswordReset";
import { RequestPasswordResetFormSchema } from "./RequestPasswordResetForm.schema";

interface RequestPasswordFormData {
  email: string;
}

export interface StepRequestPasswordResetProps {
  onSuccess: () => void;
}

const StepRequestPasswordReset = ({ onSuccess }: StepRequestPasswordResetProps) => {
  const { mutate: requestResetPassword, isPending, isSuccess, isError, error } = useRequestPasswordReset();
  const toast = useToast();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<RequestPasswordResetFormSchema>({
    resolver: yupResolver(RequestPasswordResetFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit: SubmitHandler<RequestPasswordFormData> = async data => {
    requestResetPassword(data.email);
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  useEffect(() => {
    if (isError) {
      toast.error(error?.message || "Failed to send password reset email");
    }
  }, [isError]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <header>
        <h1 className="text-2xl">Reset your password</h1>
        <h3 className="text-base text-neutral-variant-30/60">
          Enter your email and we&apos;ll send you a link to reset your password
        </h3>
      </header>

      <TextField.Root>
        <TextField.Label>Email</TextField.Label>
        <TextField.Input
          {...register("email")}
          placeholder="Enter your registered email"
          isInvalid={!!errors.email?.message}
          autoComplete="email"
          type="email"
        />
        <TextField.ErrorMessage>{errors.email?.message}</TextField.ErrorMessage>
      </TextField.Root>

      <Button size="lg" isLoading={isPending} type="submit">
        Send link to email
      </Button>
    </form>
  );
};

export default StepRequestPasswordReset;
